import type { DonationForm } from '@kuow/core';
import type { FormData, FormStep, StepValidation } from './types';

// Validation result types
export interface ValidationResult {
  isValid: boolean;
}

export interface BankPaymentFieldErrors {
  firstName?: string;
  lastName?: string;
  bankName?: string;
  routingNumber?: string;
  accountNumber?: string;
}

export interface BankPaymentValidationResult extends ValidationResult {
  fieldErrors: BankPaymentFieldErrors;
}

export interface InformationFieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  captcha?: string;
}

export interface InformationValidationResult extends ValidationResult {
  fieldErrors: InformationFieldErrors;
}

export function validateAmountStep(formData: FormData): boolean {
  const hasAmount = formData.amount !== null && formData.amount > 0;
  const hasCustomAmount = formData.customAmount !== null && formData.customAmount > 0;
  return hasAmount || hasCustomAmount;
}

export function validateGiftStep(formData: FormData, formDetails: DonationForm | null): boolean {
  const hasGifts = (formDetails?.gifts.length ?? 0) > 0;

  if (!hasGifts) {
    return true;
  }

  if (formData.noGift) {
    return true;
  }

  if (!formData.gift) {
    return false;
  }

  const selectedGift = formDetails?.gifts.find((gift) => gift.id === formData.gift?.giftId);

  if (!selectedGift) {
    return false;
  }

  const variants = selectedGift.gift_variants;

  if (variants.length <= 1) {
    // Single-variant gifts are always valid once selected.
    return true;
  }

  if (!formData.gift.variantId) {
    return false;
  }

  return variants.some((variant) => variant.id === formData.gift?.variantId);
}

export function validateBankPaymentField(field: string, value: string): string | undefined {
  if (field === 'routingNumber') {
    if (!value) {
      return 'Routing number is required';
    }
    if (!/^\d{9}$/.test(value)) {
      return 'Routing number must be exactly 9 digits';
    }
  } else if (field === 'accountNumber') {
    if (!value) {
      return 'Account number is required';
    }
  } else if (field === 'firstName') {
    if (!value.trim()) {
      return 'First name is required';
    }
  } else if (field === 'lastName') {
    if (!value.trim()) {
      return 'Last name is required';
    }
  } else if (field === 'bankName') {
    if (!value.trim()) {
      return 'Bank name is required';
    }
  }
  return undefined;
}

export function validateBankPayment(formData: FormData): BankPaymentValidationResult {
  const fieldErrors: BankPaymentFieldErrors = {};

  if (!formData.bankData) {
    return {
      isValid: false,
      fieldErrors: {
        firstName: 'First name is required',
        lastName: 'Last name is required',
        bankName: 'Bank name is required',
        routingNumber: 'Routing number is required',
        accountNumber: 'Account number is required',
      },
    };
  }

  const bank = formData.bankData;

  const firstNameError = validateBankPaymentField('firstName', bank.firstName);
  if (firstNameError) fieldErrors.firstName = firstNameError;

  const lastNameError = validateBankPaymentField('lastName', bank.lastName);
  if (lastNameError) fieldErrors.lastName = lastNameError;

  const bankNameError = validateBankPaymentField('bankName', bank.bankName);
  if (bankNameError) fieldErrors.bankName = bankNameError;

  const routingNumberError = validateBankPaymentField('routingNumber', bank.routingNumber);
  if (routingNumberError) fieldErrors.routingNumber = routingNumberError;

  const accountNumberError = validateBankPaymentField('accountNumber', bank.accountNumber);
  if (accountNumberError) fieldErrors.accountNumber = accountNumberError;

  const isValid = Object.keys(fieldErrors).length === 0;

  return { isValid, fieldErrors };
}

export function validatePaymentStep(formData: FormData): boolean {
  const hasPaymentMethod = formData.paymentMethod !== null && formData.paymentMethod.length > 0;
  
  // If payment method is card, also check that card data has been collected
  if (formData.paymentMethod === 'card') {
    return hasPaymentMethod && formData.cardData !== null;
  }
  
  // If payment method is bank, validate bank data with field-level validation
  if (formData.paymentMethod === 'bank') {
    if (!hasPaymentMethod) {
      return false;
    }
    const bankValidation = validateBankPayment(formData);
    return bankValidation.isValid;
  }
  
  // If payment method is Apple Pay, check that Apple Pay data exists and is authorized
  if (formData.paymentMethod === 'apple-pay') {
    return hasPaymentMethod && 
           formData.applePayData !== null && 
           formData.applePayData.status === 'authorized';
  }
  
  return hasPaymentMethod;
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function validateInformationField(
  field: string,
  value: string,
  requiresShipping: boolean,
  requiresEmail = true
): string | undefined {
  if (field === 'firstName') {
    if (!value.trim()) {
      return 'First name is required';
    }
  } else if (field === 'lastName') {
    if (!value.trim()) {
      return 'Last name is required';
    }
  } else if (field === 'email') {
    if (!value.trim()) {
      return requiresEmail ? 'Email is required' : undefined;
    }
    if (!isValidEmail(value)) {
      return 'Please enter a valid email address';
    }
  } else if (requiresShipping) {
    // Shipping address fields only required if gift selected
    if (field === 'street' && !value.trim()) {
      return 'Street address is required';
    } else if (field === 'city' && !value.trim()) {
      return 'City is required';
    } else if (field === 'state' && !value.trim()) {
      return 'State is required';
    } else if (field === 'country' && !value.trim()) {
      return 'Country is required';
    } else if (field === 'postalCode' && !value.trim()) {
      return 'Postal code is required';
    }
  }
  return undefined;
}

export function validateInformation(
  formData: FormData,
  requiresEmail = true,
  options?: { disableCaptcha?: boolean; alwaysShowAddress?: boolean }
): InformationValidationResult {
  const fieldErrors: InformationFieldErrors = {};
  const requiresShipping = (formData.gift !== null && !formData.noGift) || Boolean(options?.alwaysShowAddress);
  const requiresCaptcha =
    (formData.paymentMethod === 'card' || formData.paymentMethod === 'bank') &&
    !options?.disableCaptcha;

  const firstNameError = validateInformationField('firstName', formData.firstName, requiresShipping, requiresEmail);
  if (firstNameError) fieldErrors.firstName = firstNameError;

  const lastNameError = validateInformationField('lastName', formData.lastName, requiresShipping, requiresEmail);
  if (lastNameError) fieldErrors.lastName = lastNameError;

  const emailError = validateInformationField('email', formData.email, requiresShipping, requiresEmail);
  if (emailError) fieldErrors.email = emailError;

  if (requiresShipping) {
    const streetError = validateInformationField('street', formData.street, requiresShipping, requiresEmail);
    if (streetError) fieldErrors.street = streetError;

    const cityError = validateInformationField('city', formData.city, requiresShipping, requiresEmail);
    if (cityError) fieldErrors.city = cityError;

    const stateError = validateInformationField('state', formData.state, requiresShipping, requiresEmail);
    if (stateError) fieldErrors.state = stateError;

    const countryError = validateInformationField('country', formData.country, requiresShipping, requiresEmail);
    if (countryError) fieldErrors.country = countryError;

    const postalCodeError = validateInformationField('postalCode', formData.postalCode, requiresShipping, requiresEmail);
    if (postalCodeError) fieldErrors.postalCode = postalCodeError;
  }

  // Validate captcha if required (card or bank payment)
  if (requiresCaptcha && !formData.captcha.trim()) {
    fieldErrors.captcha = 'Please complete the reCAPTCHA verification';
  }

  const isValid = Object.keys(fieldErrors).length === 0;

  return { isValid, fieldErrors };
}

export function validateInformationStep(
  formData: FormData,
  requiresEmail = true,
  options?: { disableCaptcha?: boolean; alwaysShowAddress?: boolean }
): boolean {
  const validation = validateInformation(formData, requiresEmail, options);
  return validation.isValid;
}

export function canContinueFromStep(step: FormStep, validation: StepValidation): boolean {
  switch (step) {
    case 'amount':
      return validation.amount;
    case 'monthly-upsell':
      return validation.amount;
    case 'gift':
      return validation.gift;
    case 'payment':
      return validation.payment;
    case 'information':
      return validation.information;
    case 'receipt':
      return false; // Receipt is final step, no continue button
    default:
      return false;
  }
}

export function canGoBack(step: FormStep): boolean {
  return step !== 'amount' && step !== 'receipt';
}

